import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CircularProgress,
  CopyTextField,
  Dialog,
  VerticalBox,
  useSnackbarContext,
} from "@inductiveautomation/ws-components";

import { MakerLicenseSummary } from "../Licenses.types";
import { handleResponse } from "../../../utilities/api_response";
import {
  MakerLicenseSummaryItem,
  usePostGenerateActivationTokenMutation,
  GenerateTokenMakerLicenseResponse,
} from "../api/makerLicenses.api";
import { addDashToKey } from "../License/utilities";

const GenerateActivationTokenDialog = ({
  open,
  licenses,
  onClose,
  onSubmit,
}: {
  open: boolean;
  licenses: MakerLicenseSummaryItem[];
  onClose: () => void;
  onSubmit: () => void;
}) => {
  const [generateToken] = usePostGenerateActivationTokenMutation();
  const { addSnackbar } = useSnackbarContext();

  const { t } = useTranslation("translation", {
    keyPrefix: "maker_licenses.actions.generate_activation_token",
  });
  const { t: unprefixedT } = useTranslation();
  const testId = "generate-activation-token";

  const [step, setStep] = useState<"initial" | "generating" | "generated">(
    "initial"
  );

  const [activationToken, setActivationToken] = useState<string>();
  const internalOnClose = () => {
    setStep("initial");
    onClose();
  };

  const license = licenses[0] as MakerLicenseSummary | undefined;
  if (!license) return null;

  const alertDescriptionKey = step === "initial" ? "initial" : "generated";

  const generateAction = {
    testId: "generate",
    type: "submit" as const,
    label: t("steps.initial.buttons.submit"),
    onClick: () => {
      setStep("generating");
      void generateToken({
        licenseKeyPayload: {
          licenseKey: license.key,
        },
      }).then((response) => {
        handleResponse(response, {
          onSuccess: ({ data }) => {
            addSnackbar(t("steps.generated.success_message"));
            setStep("generated");
            setActivationToken(
              (data as GenerateTokenMakerLicenseResponse).activationToken
            );
          },
          onError: () => {
            addSnackbar(t("steps.generated.error_message"));
          },
          fallbackErrorMessage: t("steps.generated.error_message"),
        });
      });
    },
  };
  const doneAction = {
    testId: "done",
    type: "submit" as const,
    label: t("steps.generated.buttons.submit"),
    onClick: () => {
      setStep("initial");
      onSubmit();
    },
  };

  return (
    <Dialog
      testId={testId}
      isOpen={open}
      onClose={internalOnClose}
      showCloseButton
      headline={t(`steps.${step}.heading`)}
      collapseContentScrollGutter
      supportingText={
        step === "generating"
          ? t("steps.generating.supporting_text")
          : step === "generated"
            ? t("steps.generated.supporting_text")
            : t("steps.initial.supporting_text")
      }
      alert={
        ["initial", "generated"].includes(step)
          ? {
              id: `${testId}-alert`,
              description: t(`steps.${alertDescriptionKey}.alert`),
              variant: "warning",
              closeButtonEventHandler: () => undefined,
            }
          : undefined
      }
      contentArea={
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {step === "generating" ? (
            <CircularProgress testId="maker-licenses-generate-activation-token-loading" />
          ) : null}
          {step === "generated" ? (
            <VerticalBox gap="1.5rem" style={{ width: "100%" }}>
              <CopyTextField
                id="license_key"
                label={t("steps.generated.form.license_key")}
                value={addDashToKey(license.key)}
              />
              <CopyTextField
                id="activation_token"
                label={t("steps.generated.form.activation_token")}
                value={activationToken ?? ""}
              />
            </VerticalBox>
          ) : null}
        </div>
      }
      actions={[
        ...(step === "initial"
          ? [
              {
                testId: "cancel",
                type: "cancel" as const,
                label: unprefixedT("common.cancel"),
              },
              generateAction,
            ]
          : []),
        ...(step === "generated" ? [doneAction] : []),
      ]}
    />
  );
};

export default GenerateActivationTokenDialog;
